import {
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Group,
  Input,
  MultiSelect,
  rem,
  Select,
  Text,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconBriefcase,
  IconBuildingWarehouse,
  IconFileExport,
  IconListDetails,
  IconPackage,
  IconSearch,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React from "react";
import {
  repositoryDelivery,
  repositoryPos,
  styleTableMantine,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_POS } from "../../../config";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";

const STORAGE_KEY = "warehouse_serial_stocktake_v2";

interface ScanRow {
  id: string;
  sourceType: "lookup" | "manual";
  serial: string;
  itemCode: string;
  itemName: string;
  condition: string;
  conditionNote: string;
  expectedQuantity: number;
  scanCount: number;
  variance: number;
}

interface StocktakeState {
  completedAt: string | null;
  importedFileName: string;
  subInvCode: string;
  kyKiemKe?: number;
  rows: ScanRow[];
}

interface SerialReportRow {
  stt: number;
  maKho: string;
  maVt: string;
  tenVt: string;
  serial: string;
  trangThaiSerial: string;
  ghiChuTrangThaiSerial: string;
  soLieuPhanMem: number;
  soLuongThucTe: number;
  chenhLech: number;
}

interface SummaryReportRow {
  stt: number;
  maKho: string;
  maVt: string;
  tenVt: string;
  donViTinh: string;
  soLieuPhanMem: number;
  soLuongThucTe: number;
  chenhLech: number;
}

type ReportType = "serial" | "item";
type StocktakeReportRow = SerialReportRow | SummaryReportRow;

interface ApiSerialRow {
  branchCode?: string | null;
  branchName?: string | null;
  maKho?: string | null;
  tenKho?: string | null;
  maVt?: string | null;
  tenVT?: string | null;
  tenVt?: string | null;
  itemCode?: string | null;
  itemName?: string | null;
  maSp?: string | null;
  tenSp?: string | null;
  maBarcode?: string | null;
  serial?: string | null;
  dvt?: string | null;
  soLuong?: number | null;
  soLieuPhanMem?: number | null;
  soLuongThucTe?: number | null;
  slDaQuet?: number | null;
  chenhLech?: number | null;
  tinhTrang?: string | null;
  ghiChuTinhTrang?: string | null;
  trangThaiSerial?: string | null;
  trangThai?: string | null;
  ghiChuTrangThai?: string | null;
}

interface ApiStocktakeListResponse {
  success?: boolean;
  message?: string | null;
  data?: ApiSerialRow[] | null;
  lists?: ApiSerialRow[] | null;
  count?: number;
}

interface ReportSourceRow {
  branchCode: string;
  maKho: string;
  maVt: string;
  tenVt: string;
  serial: string;
  trangThaiSerial: string;
  ghiChuTrangThaiSerial: string;
  donViTinh: string;
  soLieuPhanMem: number;
  soLuongThucTe: number;
  chenhLech: number;
}

interface BranchOption {
  value: string;
  label: string;
  id?: string | number;
}

interface InventoryOption {
  value: string;
  label: string;
}

interface ReportSearchState {
  KyKiemKe: number;
  MaCn: string[];
  MaKho: string[];
  MaVt: string[];
  Segment2: string[];
  Segment3: string[];
  Segment4: string[];
}

const getCurrentMonth = () => new Date().getMonth() + 1;

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const month = index + 1;
  return {
    value: String(month),
    label: `Tháng ${month}`,
  };
});

const normalizeInventoryMonth = (value: unknown) => {
  const month = Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12
    ? month
    : getCurrentMonth();
};

const readUserLogin = () => {
  const rawValue =
    localStorage.getItem("userLoginHeader") || localStorage.getItem("userLogin");

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getDefaultBranchCode = (options: BranchOption[]) => {
  const userLogin = readUserLogin();

  if (!userLogin) {
    return "";
  }

  const branchCode = pickText(
    userLogin.branchCode != null ? String(userLogin.branchCode) : undefined,
    userLogin.BranchCode != null ? String(userLogin.BranchCode) : undefined,
    userLogin.maCn != null ? String(userLogin.maCn) : undefined,
    userLogin.MaCn != null ? String(userLogin.MaCn) : undefined
  );

  if (branchCode && options.some((item) => item.value === branchCode)) {
    return branchCode;
  }

  const branchId = pickText(
    userLogin.branchId != null ? String(userLogin.branchId) : undefined,
    userLogin.BranchId != null ? String(userLogin.BranchId) : undefined
  );

  if (!branchId) {
    return "";
  }

  return (
    options.find((item) => item.id != null && String(item.id) === branchId)
      ?.value || ""
  );
};

const readStocktakeState = (): StocktakeState => {
  try {
    const rawValue = localStorage.getItem(STORAGE_KEY);

    if (!rawValue) {
      return {
        completedAt: null,
        importedFileName: "",
        subInvCode: "",
        rows: [],
      };
    }

    const parsed = JSON.parse(rawValue) as Partial<StocktakeState>;

    return {
      completedAt: parsed.completedAt || null,
      importedFileName: parsed.importedFileName || "",
      subInvCode: parsed.subInvCode || "",
      kyKiemKe: normalizeInventoryMonth(parsed.kyKiemKe),
      rows: Array.isArray(parsed.rows) ? parsed.rows : [],
    };
  } catch (error) {
    console.error(error);
    return {
      completedAt: null,
      importedFileName: "",
      subInvCode: "",
      rows: [],
    };
  }
};

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getRowVariance = (row: ScanRow) => {
  if (row.sourceType === "manual") {
    return toNumber(row.scanCount);
  }

  return toNumber(row.variance);
};

const pickText = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
};

const extractApiRows = (response?: ApiStocktakeListResponse | ApiSerialRow[]) => {
  if (!response) {
    return null;
  }

  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  if (Array.isArray(response.lists)) {
    return response.lists;
  }

  return null;
};

const buildLocalSourceRows = (state: StocktakeState): ReportSourceRow[] =>
  state.rows.map((row) => ({
    branchCode: "",
    maKho: state.subInvCode,
    maVt: row.itemCode,
    tenVt: row.itemName,
    serial: row.serial,
    trangThaiSerial: row.condition,
    ghiChuTrangThaiSerial: row.conditionNote,
    donViTinh: "Chiếc",
    soLieuPhanMem:
      row.sourceType === "manual" ? 0 : toNumber(row.expectedQuantity),
    soLuongThucTe: toNumber(row.scanCount),
    chenhLech: getRowVariance(row),
  }));

const buildApiSourceRows = (
  state: StocktakeState,
  apiRows: ApiSerialRow[]
): ReportSourceRow[] =>
  apiRows.map((item) => {
    const serial = pickText(item.serial, item.maBarcode);
    const apiActualQuantity = toNumber(item.soLuongThucTe ?? item.slDaQuet);
    const apiVariance = toNumber(item.chenhLech);
    const soLuongThucTe = apiActualQuantity;
    const soLieuPhanMem =
      item.soLieuPhanMem != null
        ? toNumber(item.soLieuPhanMem)
        : item.soLuong != null
        ? toNumber(item.soLuong)
        : soLuongThucTe - apiVariance;

    return {
      branchCode: pickText(item.branchCode),
      maKho: pickText(item.maKho, state.subInvCode),
      maVt: pickText(item.maVt, item.itemCode, item.maSp),
      tenVt: pickText(item.tenVT, item.tenVt, item.itemName, item.tenSp),
      serial,
      trangThaiSerial: pickText(
        item.trangThaiSerial,
        item.tinhTrang,
        item.trangThai
      ),
      ghiChuTrangThaiSerial: pickText(item.ghiChuTinhTrang, item.ghiChuTrangThai),
      donViTinh: pickText(item.dvt) || "Chiếc",
      soLieuPhanMem,
      soLuongThucTe,
      chenhLech:
        item.chenhLech != null
          ? toNumber(item.chenhLech)
          : soLuongThucTe - soLieuPhanMem,
    };
  });

const buildSerialRows = (sourceRows: ReportSourceRow[]): SerialReportRow[] =>
  sourceRows.map((row, index) => ({
      stt: index + 1,
      maKho: row.maKho,
      maVt: row.maVt,
      tenVt: row.tenVt,
      serial: row.serial,
      trangThaiSerial: row.trangThaiSerial,
      ghiChuTrangThaiSerial: row.ghiChuTrangThaiSerial,
      soLieuPhanMem: row.soLieuPhanMem,
      soLuongThucTe: row.soLuongThucTe,
      chenhLech: row.chenhLech,
    }));

const buildSummaryRows = (sourceRows: ReportSourceRow[]): SummaryReportRow[] => {
  const grouped = new Map<
    string,
    {
      maKho: string;
      maVt: string;
      tenVt: string;
      donViTinh: string;
      soLieuPhanMem: number;
      soLuongThucTe: number;
    }
  >();

  sourceRows.forEach((row) => {
    const maKho = row.maKho;
    const maVt = row.maVt || row.serial;
    const key = `${maKho}__${maVt}`;
    const current =
      grouped.get(key) || {
        maKho,
        maVt,
        tenVt: row.tenVt,
        donViTinh: row.donViTinh,
        soLieuPhanMem: 0,
        soLuongThucTe: 0,
      };

    current.tenVt = current.tenVt || row.tenVt;
    current.donViTinh = current.donViTinh || row.donViTinh;
    current.soLieuPhanMem += row.soLieuPhanMem;
    current.soLuongThucTe += row.soLuongThucTe;
    grouped.set(key, current);
  });

  return Array.from(grouped.values()).map((row, index) => ({
    stt: index + 1,
    maKho: row.maKho,
    maVt: row.maVt,
    tenVt: row.tenVt,
    donViTinh: row.donViTinh || "Chiếc",
    soLieuPhanMem: row.soLieuPhanMem,
    soLuongThucTe: row.soLuongThucTe,
    chenhLech: row.soLuongThucTe - row.soLieuPhanMem,
  }));
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("vi-VN");
};

const filterBySearch = <T extends { maKho: string; maVt: string; tenVt: string }>(
  rows: T[],
  keySearch: string,
  warehouseCodes: string[]
) => {
  const normalizedKey = normalizeText(keySearch);

  return rows.filter((row) => {
    const matchedWarehouse =
      warehouseCodes.length === 0 || warehouseCodes.includes(row.maKho);
    const matchedKey =
      !normalizedKey ||
      normalizeText(row.maKho).includes(normalizedKey) ||
      normalizeText(row.maVt).includes(normalizedKey) ||
      normalizeText(row.tenVt).includes(normalizedKey) ||
      normalizeText((row as any).serial).includes(normalizedKey);

    return matchedWarehouse && matchedKey;
  });
};

const StocktakeReportBase = ({ reportType }: { reportType: ReportType }) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const appliedDefaultBranchRef = React.useRef(false);
  const [height, setHeight] = React.useState(0);
  const [state, setState] = React.useState<StocktakeState>(() =>
    readStocktakeState()
  );
  const [apiRows, setApiRows] = React.useState<ApiSerialRow[]>([]);
  const [search, setSearch] = React.useState<ReportSearchState>({
    KyKiemKe: getCurrentMonth(),
    MaCn: [],
    MaKho: [],
    MaVt: [],
    Segment2: [],
    Segment3: [],
    Segment4: [],
  });
  const [branchOptions, setBranchOptions] = React.useState<BranchOption[]>([]);
  const [inventoryOptions, setInventoryOptions] = React.useState<InventoryOption[]>(
    []
  );
  const [itemOptions, setItemOptions] = React.useState<ComboboxItem[]>([]);
  const [catSegment2bOptions, setCatSegment2bOptions] = React.useState<
    ComboboxItem[]
  >([]);
  const [catSegment3cOptions, setCatSegment3cOptions] = React.useState<
    ComboboxItem[]
  >([]);
  const [catSegment4dOptions, setCatSegment4dOptions] = React.useState<
    ComboboxItem[]
  >([]);
  const [keySearch, setKeySearch] = React.useState("");
  const [itemSearch, setItemSearch] = React.useState("");
  const [catSegment4dSearch, setCatSegment4dSearch] = React.useState("");
  const [debouncedItemSearch] = useDebouncedValue(itemSearch, 500);
  const [debouncedCatSegment4dSearch] = useDebouncedValue(
    catSegment4dSearch,
    500
  );
  const [isExporting, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const isSerialReport = reportType === "serial";
  const title = isSerialReport
    ? "Báo cáo kiểm kê theo chi tiết serial"
    : "Báo cáo kiểm kê theo mã vật tư";

  const reportSourceRows = React.useMemo(
    () =>
      apiRows.length
        ? buildApiSourceRows(state, apiRows)
        : buildLocalSourceRows(state),
    [apiRows, state]
  );

  const sourceRows = React.useMemo<StocktakeReportRow[]>(
    () =>
      isSerialReport
        ? buildSerialRows(reportSourceRows)
        : buildSummaryRows(reportSourceRows),
    [isSerialReport, reportSourceRows]
  );

  const data = React.useMemo(
    () =>
      filterBySearch(sourceRows, keySearch, search.MaKho).map((row, index) => ({
        ...row,
        stt: index + 1,
      })),
    [keySearch, sourceRows, search.MaKho]
  );

  const warehouseOptions = React.useMemo(
    () =>
      inventoryOptions.length
        ? inventoryOptions
        : Array.from(
            new Set(reportSourceRows.map((row) => row.maKho).filter(Boolean))
          ).map((value) => ({ value, label: value })),
    [inventoryOptions, reportSourceRows]
  );

  const updateSearchValue = React.useCallback(
    <K extends keyof ReportSearchState>(key: K, value: ReportSearchState[K]) => {
      setSearch((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const fetchBranchOptions = React.useCallback(async () => {
    if (branchOptions.length > 0) {
      return;
    }

    const response = await repositoryPos.get<MessageResponse<any[]>>(
      "api/v1/TblDmInventory/get-select-branch?auth=true"
    );

    if (response?.success) {
      const transformedData = (response.data || [])
        .filter((item) => item?.att2 && item?.text)
        .map((item) => ({
          value: String(item.att2),
          label: `${item.att2} - ${item.text}`,
          id: item.value,
        }));

      setBranchOptions(transformedData);

      if (!appliedDefaultBranchRef.current) {
        const defaultBranchCode = getDefaultBranchCode(transformedData);
        appliedDefaultBranchRef.current = true;

        if (defaultBranchCode) {
          setSearch((prev) => {
            if (prev.MaCn.length > 0 || prev.MaKho.length > 0) {
              return prev;
            }

            return {
              ...prev,
              MaCn: [defaultBranchCode],
            };
          });
        }
      }
    }
  }, [branchOptions.length]);

  const fetchInventoryOptions = React.useCallback(async () => {
    const selectedBranchIds = branchOptions
      .filter((item) => search.MaCn.includes(item.value) && item.id != null)
      .map((item) => String(item.id));

    let url = `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${
      selectedBranchIds.length > 0 ? selectedBranchIds.length * 50 : 300
    }`;

    selectedBranchIds.forEach((branchId) => {
      url += `&BranchId=${encodeURIComponent(branchId)}`;
    });

    const response = await repositoryDelivery.get<MessageResponse<any[]>>(
      url,
      true
    );

    if (response?.success) {
      const options = (response.data || [])
        .filter((item) => item?.code && item?.name)
        .map((item) => ({
          value: String(item.code),
          label: `${item.code} - ${item.name}`,
        }));

      setInventoryOptions(options);
    }
  }, [branchOptions, search.MaCn]);

  const fetchItemOptions = React.useCallback(async () => {
    let url = "api/v1/TblItem/get-list?Skip=0&Take=100";

    if (debouncedItemSearch.length > 2) {
      url += `&ListItemCode=${encodeURIComponent(debouncedItemSearch)}`;
    }

    search.MaVt.forEach((itemCode) => {
      url += `&ListItemCode=${encodeURIComponent(itemCode)}`;
    });

    const response = await repositoryPos.get<MessageResponse<any[]>>(url, true);

    if (response?.success) {
      const options = (response.data || [])
        .filter((item) => item?.itemCode && item?.itemName)
        .map((item) => ({
          label: `${item.itemCode} - ${item.itemName}`,
          value: String(item.itemCode),
        }));

      setItemOptions(
        Array.from(new Map(options.map((item) => [item.value, item])).values())
      );
    }
  }, [debouncedItemSearch, search.MaVt]);

  const fetchCatSegment2bOptions = React.useCallback(async () => {
    const response = await repositoryPos.get<MessageResponse<any[]>>(
      "/api/v1/TblCatSegment2b/get-all",
      true
    );

    if (response?.success) {
      const options = (response.data || [])
        .filter((item) => item?.codeString && item?.name)
        .map((item) => ({
          label: `${item.codeString} - ${item.name}`,
          value: String(item.codeString),
        }));

      setCatSegment2bOptions(options);
    }
  }, []);

  const fetchCatSegment3cOptions = React.useCallback(async () => {
    const response = await repositoryPos.get<MessageResponse<any[]>>(
      "/api/v1/TblCatSegment3c/get-all",
      true
    );

    if (response?.success) {
      const options = (response.data || [])
        .filter((item) => {
          if (!item?.codeString || !item?.name) {
            return false;
          }

          if (!search.Segment2.length) {
            return true;
          }

          return search.Segment2.includes(String(item.segment2CodeString));
        })
        .map((item) => ({
          label: `${item.codeString} - ${item.name}`,
          value: String(item.codeString),
        }));

      setCatSegment3cOptions(options);
    }
  }, [search.Segment2]);

  const fetchCatSegment4dOptions = React.useCallback(async () => {
    let url = "/api/v1/TblCatSegment4d/get-list?Skip=0&Take=100";

    search.Segment3.forEach((item) => {
      url += `&listCodeSegmentString3=${encodeURIComponent(item)}`;
    });

    if (debouncedCatSegment4dSearch.length > 2) {
      url += `&KeySearch=${encodeURIComponent(debouncedCatSegment4dSearch)}`;
    }

    const response = await repositoryPos.get<MessageResponse<any[]>>(url, true);

    if (response?.success) {
      const options = (response.data || [])
        .filter((item) => item?.codeString && item?.name)
        .map((item) => ({
          label: `${item.codeString} - ${item.name}`,
          value: String(item.codeString),
        }));

      setCatSegment4dOptions(options);
    }
  }, [debouncedCatSegment4dSearch, search.Segment3]);

  const buildReportQuery = React.useCallback(
    (includePaging: boolean) => {
      const params = new URLSearchParams();

      if (includePaging) {
        params.append("Skip", "0");
        params.append("Take", "100000");
      }

      params.append("KyKiemKe", String(search.KyKiemKe));

      search.MaCn.forEach((branchCode) => {
        params.append("MaCn", branchCode);
      });
      search.MaKho.forEach((warehouseCode) => {
        params.append("MaKho", warehouseCode);
      });
      search.MaVt.forEach((itemCode) => {
        params.append("MaVt", itemCode);
      });
      search.Segment2.forEach((item) => {
        params.append("Segment2", item);
      });
      search.Segment3.forEach((item) => {
        params.append("Segment3", item);
      });
      search.Segment4.forEach((item) => {
        params.append("Segment4", item);
      });

      const trimmedKeySearch = keySearch.trim();
      if (trimmedKeySearch) {
        if (isSerialReport) {
          params.append("Serial", trimmedKeySearch);
        } else {
          params.append("KeySearch", trimmedKeySearch);
        }
      }

      const query = params.toString();
      return query ? `?${query}` : "";
    },
    [isSerialReport, keySearch, search]
  );

  const fetchData = React.useCallback(async () => {
    setState(readStocktakeState());

    if (search.MaKho.length === 0) {
      NotificationExtension.Warn("Vui lòng chọn kho trước khi tìm kiếm dữ liệu.");
      return;
    }

    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);

    try {
      const url = buildReportQuery(true);
      const endpoint = isSerialReport
        ? "/api/v1/TblInventorySerialOnHand/get-list-scan-result"
        : "/api/v1/TblInventorySerialOnHand/get-list-scan-result-by-item";

      const response = await repositoryPos.get<ApiStocktakeListResponse | ApiSerialRow[]>(
        `${endpoint}${url}`,
        true
      );
      const responseRows = extractApiRows(response);

      if (responseRows) {
        setApiRows(responseRows);
        return;
      }

      setApiRows([]);
      setIsError(true);
      NotificationExtension.Fails(
        !Array.isArray(response) && response?.message
          ? response.message
          : "Không thể tải dữ liệu kiểm kê theo chi nhánh."
      );
    } catch (error) {
      console.error(error);
      setApiRows([]);
      setIsError(true);
      NotificationExtension.Fails("Không thể tải dữ liệu kiểm kê theo chi nhánh.");
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  }, [buildReportQuery, isSerialReport, search.MaKho.length]);

  const serialColumns = React.useMemo<MRT_ColumnDef<SerialReportRow>[]>(
    () => [
      { accessorKey: "stt", header: "STT", size: 80 },
      { accessorKey: "maKho", header: "Mã Kho", size: 140 },
      { accessorKey: "maVt", header: "Mã VT", size: 160 },
      { accessorKey: "tenVt", header: "Tên VT", size: 300 },
      { accessorKey: "serial", header: "Serial", size: 180 },
      { accessorKey: "trangThaiSerial", header: "Trạng thái Serial", size: 220 },
      {
        accessorKey: "ghiChuTrangThaiSerial",
        header: "Ghi chú Trạng thái Serial",
        size: 260,
      },
      { accessorKey: "soLieuPhanMem", header: "Số liệu phần mềm", size: 170 },
      { accessorKey: "soLuongThucTe", header: "Số lượng thực tế", size: 170 },
      { accessorKey: "chenhLech", header: "Chênh lệch", size: 140 },
    ],
    []
  );

  const summaryColumns = React.useMemo<MRT_ColumnDef<SummaryReportRow>[]>(
    () => [
      { accessorKey: "stt", header: "STT", size: 80 },
      { accessorKey: "maKho", header: "Mã Kho", size: 140 },
      { accessorKey: "maVt", header: "Mã VT", size: 160 },
      { accessorKey: "tenVt", header: "Tên VT", size: 320 },
      { accessorKey: "donViTinh", header: "Đơn vị tính", size: 140 },
      { accessorKey: "soLieuPhanMem", header: "Số liệu phần mềm", size: 170 },
      { accessorKey: "soLuongThucTe", header: "Số lượng thực tế", size: 170 },
      { accessorKey: "chenhLech", header: "Chênh lệch", size: 140 },
    ],
    []
  );

  const table = useMantineReactTable({
    columns: isSerialReport
      ? (serialColumns as MRT_ColumnDef<any>[])
      : (summaryColumns as MRT_ColumnDef<any>[]),
    data,
    enableColumnActions: true,
    enableColumnFilters: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableSorting: true,
    enableStickyHeader: true,
    initialState: {
      density: "xs",
      showColumnFilters: false,
      columnPinning: { left: ["stt", "maKho", "maVt"] },
    },
    state: {
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
    },
    localization: _localization,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    mantineTableBodyCellProps: () => ({
      style: styleTableMantine,
    }),
    mantineTopToolbarProps: {
      style: { display: "none" },
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      radius: "xl",
      rowsPerPageOptions: ["20", "50", "100"],
      showRowsPerPage: true,
      size: "lg",
      withEdges: true,
    },
    paginationDisplayMode: "pages",
  });

  React.useEffect(() => {
    const handleResize = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      setHeight(window.innerHeight - (155 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    void fetchBranchOptions();
  }, [fetchBranchOptions]);

  React.useEffect(() => {
    updateSearchValue("MaKho", []);
    setInventoryOptions([]);
    if (search.MaCn.length > 0 && branchOptions.length > 0) {
      void fetchInventoryOptions();
    }
  }, [
    branchOptions.length,
    fetchInventoryOptions,
    search.MaCn,
    updateSearchValue,
  ]);

  React.useEffect(() => {
    if (debouncedItemSearch.length > 2 || search.MaVt.length > 0) {
      void fetchItemOptions();
    }
  }, [debouncedItemSearch, fetchItemOptions, search.MaVt.length]);

  React.useEffect(() => {
    void fetchCatSegment2bOptions();
  }, [fetchCatSegment2bOptions]);

  React.useEffect(() => {
    void fetchCatSegment3cOptions();
  }, [fetchCatSegment3cOptions]);

  React.useEffect(() => {
    void fetchCatSegment4dOptions();
  }, [fetchCatSegment4dOptions]);

  const handleRefresh = () => {
    void fetchData();
  };

  const handleExport = async () => {
    open();

    try {
      const token = localStorage.getItem("token");
      const endpoint = isSerialReport
        ? "/api/v1/TblInventorySerialOnHand/export-excel-scan-result"
        : "/api/v1/TblInventorySerialOnHand/export-excel-scan-result-by-item";
      const response = await fetch(
        `${BASE_API_POS}${endpoint}${buildReportQuery(false)}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token?.replace(/"/g, "")}`,
            "Content-Type": "application/vnd.ms-excel",
          },
        }
      );

      if (!response.ok) {
        const errorResponse = await response.json().catch(() => null);
        throw new Error(
          errorResponse?.message || "Đã xảy ra lỗi khi xuất dữ liệu!"
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = isSerialReport
        ? "bao_cao_kiem_ke_theo_serial.xlsx"
        : "bao_cao_kiem_ke_theo_ma_vat_tu.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      NotificationExtension.Success("Export data thành công!");
    } catch (error: any) {
      console.error(error);
      NotificationExtension.Fails(
        error?.message || "Đã xảy ra lỗi không xác định!"
      );
    } finally {
      close();
    }
  };

  return (
    <Box>
      <LoadingOverlayCustom visible={isLoading || isExporting} />
      <div
        ref={headerRef}
        style={{ border: "1px solid #dee2e6", padding: "10px" }}
      >
        <Group justify="space-between" gap={0}>
          <_breadcrumb />
          <Flex direction="row" gap={10}>
            <Button variant="outline" onClick={handleRefresh}>
              Tải lại dữ liệu
            </Button>
            <Button
              leftSection={
                <IconFileExport style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={handleExport}
            >
              Export Excel
            </Button>
          </Flex>
        </Group>

        <Text fw={700} mt={10}>
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          File đã nạp: {state.importedFileName || "Chưa có dữ liệu"} | Hoàn thành:{" "}
          {formatDateTime(state.completedAt) || "Chưa hoàn thành"}
        </Text>

        <Grid mt={10}>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <Select
              placeholder="Tháng kiểm kê"
              data={MONTH_OPTIONS}
              value={String(search.KyKiemKe)}
              allowDeselect={false}
              onChange={(value) => {
                updateSearchValue("KyKiemKe", normalizeInventoryMonth(value));
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <Input
              placeholder="Nhập từ khóa tìm kiếm"
              leftSection={<IconSearch size={20} color="#15aabf" />}
              value={keySearch}
              onChange={(event) => setKeySearch(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <MultiSelect
              placeholder="Kho"
              clearable
              searchable
              data={warehouseOptions}
              value={search.MaKho}
              leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
              onDropdownOpen={() => {
                void fetchInventoryOptions();
              }}
              onChange={(value) => {
                updateSearchValue("MaKho", value);
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <MultiSelect
              placeholder="Mã sản phẩm"
              clearable
              searchable
              data={itemOptions}
              searchValue={itemSearch}
              onSearchChange={setItemSearch}
              value={search.MaVt}
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              onChange={(value) => {
                updateSearchValue("MaVt", value);
              }}
              nothingFoundMessage="Vui lòng nhập ít nhất 3 kí tự !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <MultiSelect
              placeholder="Ngành"
              clearable
              searchable
              data={catSegment2bOptions}
              value={search.Segment2}
              leftSection={<IconBriefcase size={20} color="#15aabf" />}
              onChange={(value) => {
                updateSearchValue("Segment2", value);
                updateSearchValue("Segment3", []);
                updateSearchValue("Segment4", []);
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <MultiSelect
              placeholder="Loại"
              clearable
              searchable
              data={catSegment3cOptions}
              value={search.Segment3}
              leftSection={<IconPackage size={20} color="#15aabf" />}
              onChange={(value) => {
                updateSearchValue("Segment3", value);
                updateSearchValue("Segment4", []);
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
            <MultiSelect
              placeholder="Chủng"
              clearable
              searchable
              data={catSegment4dOptions}
              searchValue={catSegment4dSearch}
              onSearchChange={setCatSegment4dSearch}
              value={search.Segment4}
              leftSection={<IconListDetails size={20} color="#15aabf" />}
              onChange={(value) => {
                updateSearchValue("Segment4", value);
              }}
              nothingFoundMessage="Không tìm thấy dữ liệu !"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 1 }}>
            <Button
              color="blue"
              variant="outline"
              leftSection={<IconSearch size={16} />}
              onClick={() => {
                void fetchData();
              }}
              fullWidth
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>

      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export const SerialStocktakeDifferenceReport = () => (
  <StocktakeReportBase reportType="serial" />
);

export const SerialStocktakeSummaryReport = () => (
  <StocktakeReportBase reportType="item" />
);
